import { ActionMapper } from 'easy-peasy';
import { StoreState } from './store/store';

export type GetAccessTokenProps = {
  forceRefresh?: boolean;
};

export interface VideoPlayerProps {
  videoGUID: string;
  userGUID?: string;
  entityId?: string;
  recommendationId?: string | null;
  autoplay?: boolean;
  language?: 'en_US' | 'es_US' | 'fr_FR' | string;
  isBODi?: boolean;

  /**
   * If passed, the player invokes this function every time before calling the Video API.
   **/
  getAccessToken?: (params?: GetAccessTokenProps) => Promise<{ accessToken: string; tokenType: string }>;

  /**
   * Called when the player can be closed after playback. */
  closeCallback?: () => any;

  /**
   * Expects a function that's called with all player actions as an argument.
   **/
  actionsRef?: (actions: ActionMapper<StoreState, any>) => any;

  onPlay?: () => any;
  onPause?: () => any;
  onSeek?: (timecode: number) => any;
  onProgress?: (timecode: number) => any;
  onAnyAction?: (action: { type: string; payload: any }) => any;

  /**
   * Allows specifying arbitrary VHS properties
   * that will get sent with each heartbeat payload.
   **/
  extraHeartbeatParams?: { [key: string]: unknown };

  config: {
    videoApiRetryAttempts?: number;
    vhsApiUrl: string;
    vhsApiKey: string;
    cocoaApiUrl?: string;
    cocoaApiKey?: string;
    convivaCustomerKey: string;
    chromecastAppId?: string;
    playbackControlsEnabled?: boolean;
    maxBitrateForEdge?: number;

    /**
     * Hides the Resume Overlay regardless of whether the resume point is valid.
     * @default false
     **/
    skipResume?: boolean;

    /**
     * Activates debug mode
     * @default false
     **/
    debug?: boolean;

    /**
     * Activates Redux logger.
     * @default false
     **/
    logging?: boolean;

    /**
     * Sets the 'bodweb' header in API requests used to authorize web tokens.
     * @default true
     **/
    useBodwebHeader?: boolean;

    /**
     * Activates the 'x-username' header for Video API requests
     * @default false
     **/
    simulateVideoApiAuthorizer?: boolean;
  };
}

export enum VideoType {
  WORKOUT = 'WORKOUT',
  MODIFIER = 'MODIFIER',
}

export enum Time {
  TW0_MINUTES_IN_SEC = 120,
  THREE_MINUTES_IN_SEC = 180,
}

export enum PlayerStatus {
  Uninitialized = 'Uninitialized',
  Initialized = 'Initialized',
  Loading = 'Loading',
  Stopped = 'Stopped',
  Playing = 'Playing',
  Paused = 'Paused',
  Ended = 'Ended',
  Buffering = 'Buffering',
  Errored = 'Errored',
}

export enum SubtitleTextSizes {
  Smallest = 'Smallest',
  Small = 'Small',
  Normal = 'Normal',
  Large = 'Large',
  Largest = 'Largest',
}

export enum SubtitleTextWeights {
  Regular = 'Regular',
  Bold = 'Bold',
}

export enum SubtitleTextColors {
  Red = '#FF0000',
  Orange = '#FFCC00',
  Yellow = '#FFFF00',
  White = '#FFFFFF',
  Gray = '#666666',
  Black = '#000000',
  Green = '#00FF00',
  Cyan = '#00FFFF',
  Blue = '#0000FF',
}

export type AudioTrack = {
  id: number;
  type: 'AUDIO';
  groupId: string;
  name: string;
  default: boolean;
  autoselect: boolean;
  forced: boolean;
  lang: string;
  url: string;
  audioCodec: string;
};

export type CuesParsedData = {
  type: 'captions' | 'subtitles';
  track: string;
  cues: VTTCue[];
};

export type UserSettings = {
  audio: string;
  subtitle: string;
  subtitleTextSize: SubtitleTextSizes;
  subtitleTextWeight: SubtitleTextWeights;
  subtitleTextColor: SubtitleTextColors;
  volumeLevel: number;
};

export enum StorageKeys {
  SETTINGS = 'WebVideoPlayer.settings',
}
export namespace CocoaAPI {
  export type Playback = {
    metadata: {
      guid: string;
      videoId: string;
      title: string;
      description: string;
      duration: number;
      type: string[];
      resumePoint: number;
      language: string;
      workoutTypes: any[];
      trainers: Array<{
        firstName: string;
        lastName: string;
        shortBio: {
          rendered: string;
          raw: string;
        };
      }>;
    };
    urls: {
      playbackStandard: string;
    };
    modifiers: {
      guid: string;
      videoId: string;
      title: string;
      url: string;
    }[];
    thumbnail: {
      image: string;
      width: number;
      height: number;
      fileSize: number;
    };
    thumbnail_previews: {
      bifs: any[];
      thumbnails: Array<{
        url: string;
        format: string;
      }>;
      webvtts: Array<{
        url: string;
        format: string;
      }>;
    };
    thumbnail_cast: {
      url: string;
      height: number;
      width: number;
    };
    videoAnalytics: {
      assetName: string;
    };
  };
}

export type VideoItem = {
  type: VideoType;
  title: string;
  guid: string;
  url: string;
  thumbnailUrl?: string;
  resumePoint?: number;
};

export type VHSEvent = VideoPlayerProps['extraHeartbeatParams'] & {
  audioTrack: string;
  duration: number;
  eventDetails: VHSEventDetails;
  eventType: VHSEventType;
  heartbeatInterval: number;
  platform: 'web';
  platformDetails: string;
  profileID: string;
  subtitlesOn: boolean;
  subtitleTrack: string | undefined;
  timecode: number;
  timestamp: string;
  videoGuid: string;
  playerVisible: boolean;
  entityId?: string;
  recommendationId?: string;
};

export enum VHSEventType {
  start = 'start',
  heartbeat = 'heartbeat',
  play = 'play',
  pause = 'pause',
  stop = 'stop',
}

export enum VHSEventDetails {
  system_generated = 'system generated',
  user_generated = 'user generated',
  media_start = 'media start',
  media_end = 'media end',
  resume_show = 'resume show',
  resume_hide = 'resume hide',
  source_video = 'source video',
  modifier_video = 'modifier video',
}

export type SendVHSEventPayload = {
  eventType: VHSEventType;
  eventDetails: VHSEventDetails;
};

export type SVGIconProps = {
  width?: number;
  height?: number;
  fill?: string;
};

export type Locale = {
  name: string;
  translations: {
    General: {
      On: string;
      Off: string;
    };
    BottomControls: {
      NowPlaying: string;
      PlayingOn: (device: string) => string;
    };
    ContextMenuOverlay: {
      Version: (version: string) => string;
      PoweredByBeachbody: string;
    };
    ErrorOverlay: {
      ErrorCode: (code: number) => string;
      CannotBePlayed: string;
      PleaseTryAgain: string;
    };
    LanguageOptions: {
      Subtitles: string;
      SubtitleOptions: string;
      Audio: string;
    };
    SubtitleOptions: {
      FontSize: string;
      TextSizeOption: (textSizeOption: SubtitleTextSizes) => string;
      FontColor: string;
      BoldText: string;
    };
    ModifierIndicator: {
      Modifier: string;
    };
    ResumeOverlay: {
      AboutToPlay: string;
      ResumeVideo: string;
      StartOver: string;
    };
  };
};
